import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MastersService } from './masters.service';
import { InitialsPipe } from '../pipe/initials.pipe';

@Component({
  selector: 'app-masters',
  standalone: true,
  imports: [CommonModule, FormsModule, InitialsPipe],
  templateUrl: './masters.component.html',
  styleUrls: ['./masters.component.scss'],
})
export class MastersComponent {
  private mastersService = inject(MastersService);

  products: any[] = [];
  selectedProductIndex: number | null = null;
  selectedProductForModule: string = '';
  showProductModal = false;
  showModuleModal = false;

  // Toast
  showToast = false;
  toastMessage = '';

  // Product modal vars
  editIndex: number | null = null;
  productName = '';
  productDescription = '';

  // Module modal vars
  editModuleIndex: number | null = null;
  moduleName = '';
  showDeleteModal = false;
  deleteType: 'product' | 'module' | null = null;
  deleteTargetIndex: number | null = null;

  ngOnInit(): void {
    this.loadProducts();
  }

  // ✅ Toast helper
  showSuccess(message: string) {
    this.toastMessage = message;
    this.showToast = true;
    setTimeout(() => (this.showToast = false), 2500);
  }

  // ✅ Product CRUD
  loadProducts() {
  this.mastersService.getProducts().subscribe({
    next: (res) => {
      this.products = res;

      // uto-select first project and load its modules
      if (this.products.length > 0 && this.selectedProductIndex === null) {
        this.selectProduct(0);
      }
    },
    error: (err) => console.error('Failed to load products', err),
  });
}

  openProductModal(index: number | null = null) {
    this.editIndex = index;
    if (index !== null) {
      const product = this.products[index];
      this.productName = product.name;
      this.productDescription = product.description;
    } else {
      this.productName = '';
      this.productDescription = '';
    }
    this.showProductModal = true;
  }

  closeProductModal() {
    this.showProductModal = false;
  }

  saveProduct() {
    const payload = {
      name: this.productName.trim(),
      description: this.productDescription.trim(),
    };

    if (!payload.name) return;

    if (this.editIndex !== null) {
      const product = this.products[this.editIndex];
      this.mastersService.updateProduct(product._id, payload).subscribe(() => {
        this.loadProducts();
        this.showSuccess('Product updated successfully');
        this.closeProductModal();
      });
    } else {
      this.mastersService.createProduct(payload).subscribe(() => {
        this.loadProducts();
        this.showSuccess('Product created successfully');
        this.closeProductModal();
      });
    }
  }

  deleteProduct(index: number) {
    this.deleteType = 'product';
    this.deleteTargetIndex = index;
    this.showDeleteModal = true;
  }

  selectProduct(index: number) {
    this.selectedProductIndex = index;
    const selectedProduct = this.products[index];

    this.mastersService.getModules(selectedProduct._id!).subscribe({
      next: (modules) => {
        this.products[index].modules = modules; // keep objects, not just names
      },
      error: (err) => console.error('Failed to load modules', err),
    });
  }

  // ✅ Module CRUD
  openModuleModal(index: number | null = null) {
    this.editModuleIndex = index;
    if (index !== null && this.selectedProductIndex !== null) {
      this.moduleName =
        this.products[this.selectedProductIndex].modules[index].moduleName;
    } else {
      this.moduleName = '';
    }
    this.showModuleModal = true;
  }

  closeModuleModal() {
    this.showModuleModal = false;
  }

  saveModule() {
    // Ensure a project is selected (either manually or via current selection)
    if (!this.selectedProductForModule && this.selectedProductIndex === null) return;

    const product =
      this.products.find((p) => p.name === this.selectedProductForModule) ||
      this.products[this.selectedProductIndex!];

    const payload = { moduleName: this.moduleName.trim() };
    if (!payload.moduleName) return;

    // EDIT EXISTING MODULE
    if (this.editModuleIndex !== null) {
      const module = product.modules[this.editModuleIndex];

      if (!module || !module._id) {
        console.error('Module ID not found for update');
        return;
      }

      this.mastersService.updateModule(module._id, payload).subscribe({
        next: () => {
          this.loadProducts();
          this.getModulesForProject(product._id);
          this.showSuccess('Module updated successfully');
          this.closeModuleModal();
        },
        error: (err) => {
          console.error('Failed to update module', err);
        },
      });
    }
    // CREATE NEW MODULE
    else {
      this.mastersService.createModule(product._id, payload).subscribe({
        next: () => {
          this.loadProducts();
          this.getModulesForProject(product._id);
          this.showSuccess('Module created successfully');
          this.closeModuleModal();
        },
        error: (err) => {
          console.error('Failed to create module', err);
        },
      });
    }
  }

  getModulesForProject(projectId: string) {
  this.mastersService.getModules(projectId).subscribe({
    next: (modules) => {
      if (this.selectedProductIndex !== null) {
        this.products[this.selectedProductIndex].modules = modules;
      }
    },
    error: (err) => {
      console.error('Failed to fetch modules for project', err);
    },
  });
}


  deleteModule(index: number) {
    this.deleteType = 'module';
    this.deleteTargetIndex = index;
    this.showDeleteModal = true;
  }

  confirmDelete() {
    if (this.deleteType === 'product' && this.deleteTargetIndex !== null) {
      const product = this.products[this.deleteTargetIndex];
      if (!product?._id) return;

      this.mastersService.deleteProduct(product._id).subscribe(() => {
        this.loadProducts();
        this.showSuccess('Project deleted successfully');
        this.closeDeleteModal();
      });
    }

    if (this.deleteType === 'module' && this.deleteTargetIndex !== null && this.selectedProductIndex !== null) {
      const product = this.products[this.selectedProductIndex];
      const module = product.modules[this.deleteTargetIndex];
      if (!module?._id) return;

      this.mastersService.deleteModule(module._id).subscribe(() => {
        this.loadProducts();
        this.showSuccess('Module deleted successfully');
        this.closeDeleteModal();
      });
    }
  }

closeDeleteModal() {
  this.showDeleteModal = false;
  this.deleteType = null;
  this.deleteTargetIndex = null;
}
}
